import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import {
  m as motion,
  MAnimatePresence as AnimatePresence,
} from "../../shared/components/Motion";
import axios from "axios";
import { db } from "../../config/firebase";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import "./chatbot.css";
import STEPS from "./steps";
import { useAboutData } from "../../shared/hooks";
import { useAdmin } from "../../features/admin/AdminProvider";
import {
  chatWindowVariants,
  chatMsgVariants,
} from "../../shared/utils/animationVariants";
import { cld } from "../../shared/utils";

const SESSION_KEY = "chatbot_opened";
const ANSWERS_KEY = "chatbot_answers"; // persisted chat answers (object)
const SENT_KEY = "chatbot_sent"; // "true" once email dispatched
const HISTORY_KEY = "chatbot_history"; // persisted full message history
const HISTORY_TS_KEY = "chatbot_history_ts"; // timestamp when history was saved
const STEP_KEY = "chatbot_step"; // current step id for mid-conversation restore
const HISTORY_TTL = 1000 * 60 * 60 * 24; // 24 hours — matches Firebase cache TTL

/** Clear all chatbot localStorage keys */
const clearChatStorage = () => {
  [ANSWERS_KEY, SENT_KEY, HISTORY_KEY, HISTORY_TS_KEY, STEP_KEY].forEach((k) =>
    localStorage.removeItem(k),
  );
};

// Maps step id → key inside the stored answers object
const STEP_FIELD = {
  "who-are-you": "whoAreYou",
  resume: "resumeAction",
  "ask-name-rec": "name",
  "ask-company": "company",
  "ask-name-dev": "nameOrLinkedin",
  "ask-github": "github",
  "ask-name-gen": "name",
  rating: "rating",
  feedback: "feedback",
  "rating-back": "ratingUpdated",
  "feedback-back": "feedbackUpdated",
};

// Steps where we ask for the user's name
const NAME_STEP_IDS = ["ask-name-rec", "ask-name-dev", "ask-name-gen"];

/**
 * Merge one answer into the localStorage draft.
 * Called immediately when the user responds so data survives a browser close.
 */
const persistChatAnswer = (stepId, text) => {
  const field = STEP_FIELD[stepId];
  if (!field || !text) return;
  try {
    const existing = JSON.parse(localStorage.getItem(ANSWERS_KEY) || "{}");
    existing[field] = text;
    localStorage.setItem(ANSWERS_KEY, JSON.stringify(existing));
  } catch (_) {}
};

/**
 * Fire-and-forget: send whatever chatbot answers are in localStorage to the
 * Netlify function, then mark as sent so we never double-send.
 * Safe to call multiple times — returns early if already sent or empty.
 */
const sendChatbotData = async (googleUser = null) => {
  try {
    if (localStorage.getItem(SENT_KEY) === "true") return;
    const raw = localStorage.getItem(ANSWERS_KEY);
    if (!raw) return;
    const answers = JSON.parse(raw);
    if (!Object.values(answers).some(Boolean)) return; // nothing meaningful
    const dateTime = new Date().toLocaleString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    const googleFields = googleUser
      ? {
          googleName: googleUser.displayName || null,
          googleEmail: googleUser.email || null,
          googlePhotoUrl: googleUser.photoURL || null,
        }
      : {};
    await Promise.all([
      axios.post("/.netlify/functions/send-visitor-details", {
        source: "chatbot",
        ...answers,
        ...googleFields,
      }),
      addDoc(collection(db, "Portfolio Events"), {
        source: "chatbot",
        ...answers,
        ...googleFields,
        timestamp: serverTimestamp(),
        dateTime,
      }),
    ]);
    localStorage.setItem(SENT_KEY, "true");
  } catch (_) {
    // Never degrade UX — silently ignore network/parse errors
  }
};

/** How long the typing indicator shows before the bot message appears */
const TYPING_MS = 350;

const stepById = (id) => STEPS.find((s) => s.id === id);

/** Cloudinary public-ID → viewable delivery URL */
const resolveView = (val) => {
  if (!val) return "";
  if (val.startsWith("http")) return val;
  return cld(val, 1000);
};

/** Cloudinary public-ID → force-download URL */
const resolveDownload = (val) => {
  if (!val) return "";
  if (val.startsWith("http")) return val;
  return `https://res.cloudinary.com/dc2dtlr4u/image/upload/fl_attachment/${val}`;
};

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [editingMsgId, setEditingMsgId] = useState(null);
  const [editHoverStar, setEditHoverStar] = useState(0);
  const [stepId, setStepId] = useState(null);
  const [history, setHistory] = useState([]); // { role, text, id, msgStepId? }
  const [phase, setPhase] = useState("idle"); // 'typing' | 'waiting' | 'idle'
  const [inputValue, setInputValue] = useState("");
  const [hoverRating, setHoverRating] = useState(0);
  const [isDone, setIsDone] = useState(false);
  const [nudge, setNudge] = useState(false);
  const [showScroll, setShowScroll] = useState(false);
  const [ipLocation, setIpLocation] = useState(null);
  const [keyboardOffset, setKeyboardOffset] = useState(0);

  const msgEndRef = useRef(null);
  const keyRef = useRef(0);
  const timerRef = useRef(null);
  const windowRef = useRef(null);
  const bubbleRef = useRef(null);
  const editInputRef = useRef(null);
  // Tracks the last step that the machine has already processed, so closing
  // and re-opening mid-conversation doesn't replay the current step.
  const processedStepRef = useRef(null);
  // Capture STEP_KEY synchronously at render time — before any useEffect can
  // accidentally clear it (effects with phase==="idle" run before mount restore).
  const initStepKeyRef = useRef(localStorage.getItem(STEP_KEY));

  const { data: aboutData, loading: aboutLoading } = useAboutData();
  const {
    user,
    isAdmin,
    adminMode,
    closeThemesPanel,
    themesPanelOpen,
    adminPanelOpen,
    closeAdminPanel,
  } = useAdmin();
  // Always holds the latest Firebase auth user without causing effect re-runs
  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);
  // ── Close chatbot when themes panel or admin panel opens ──────────────────────
  useEffect(() => {
    if (themesPanelOpen || adminPanelOpen) {
      clearTimeout(timerRef.current);
      setIsOpen(false);
    }
  }, [themesPanelOpen, adminPanelOpen]);
  // ── Fetch approximate IP location for header display ─────────────────────
  useEffect(() => {
    if (
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1"
    ) {
      return;
    }

    axios
      .get("https://ipapi.co/json/")
      .then((res) => {
        const { city, country_name } = res.data || {};
        const loc = [city, country_name].filter(Boolean).join(", ");
        if (loc) setIpLocation(loc);
      })
      .catch(() => {}); // non-critical — silently ignore
  }, []);

  // ── Persist history to localStorage whenever it changes ──────────────────
  useEffect(() => {
    if (history.length > 0) {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
      localStorage.setItem(HISTORY_TS_KEY, Date.now().toString());
    }
  }, [history]);

  // ── Persist STEP_KEY only when actively waiting for user input ────────────
  // Only SAVES — never clears. Clearing is done imperatively in advance() and
  // the terminal step handler so the effect's initial phase==="idle" run can't
  // wipe the key before the mount restore effect reads it.
  useEffect(() => {
    if (phase === "waiting" && stepId && !isDone) {
      localStorage.setItem(STEP_KEY, stepId);
    }
  }, [phase, stepId, isDone]);

  // ── On mount: restore history and resume conversation ────────────────────
  useEffect(() => {
    const ts = parseInt(localStorage.getItem(HISTORY_TS_KEY) || "0");
    if (ts && Date.now() - ts > HISTORY_TTL) {
      clearChatStorage();
      return;
    }

    const saved = JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]");
    if (saved.length === 0) return;

    keyRef.current = Math.max(...saved.map((m) => m.id), 0) + 1;
    setHistory(saved);

    const alreadySent = localStorage.getItem(SENT_KEY) === "true";
    if (alreadySent) {
      setIsDone(true);
      return;
    }

    // ── PRIMARY: STEP_KEY restore ─────────────────────────────────────────
    // initStepKeyRef was captured synchronously before any effect could clear
    // it, so this always reflects the value written on the previous session.
    // STEP_KEY is only written when phase==="waiting" AND after advance() clears
    // it, ensuring the saved step is always unanswered and its bot message is
    // already in history.
    const savedStepId = initStepKeyRef.current;
    if (savedStepId) {
      const step = stepById(savedStepId);
      if (step && step.type !== "message") {
        processedStepRef.current = savedStepId;
        setStepId(savedStepId);
        setPhase("waiting");
        return;
      }
    }

    // ── FALLBACK: infer position from history ─────────────────────────────
    // Handles every gap case: refresh during typing indicator, refresh after
    // advance() cleared STEP_KEY but before next phase==="waiting" fires, etc.
    const lastMsg = [...saved].reverse().find(Boolean);
    if (!lastMsg) return;

    if (lastMsg.role === "user") {
      // User answered the last question; step machine hasn't fired for next step.
      // Compute the next step from the user's answer and let step machine run it.
      const answeredStep = stepById(lastMsg.msgStepId);
      if (answeredStep) {
        const nextId =
          typeof answeredStep.next === "function"
            ? answeredStep.next(lastMsg.text)
            : answeredStep.next;
        if (nextId) setStepId(nextId); // processedStepRef stays null → step machine replays
      }
    } else {
      // Last message is a bot message. Find which step it belongs to.
      const matchingStep = STEPS.find((s) => s.message === lastMsg.text);
      if (!matchingStep) return;
      if (matchingStep.type !== "message") {
        // Bot was waiting for user on an interactive step — restore waiting state.
        processedStepRef.current = matchingStep.id;
        setStepId(matchingStep.id);
        setPhase("waiting");
      } else if (matchingStep.next !== null) {
        // Bot was mid-auto-advance — let step machine replay the next step.
        const nextId =
          typeof matchingStep.next === "function"
            ? matchingStep.next(null)
            : matchingStep.next;
        setStepId(nextId);
      }
      // next===null means terminal step was showing — conversation was ending;
      // no need to restore, user will start fresh next open.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Track scroll to lift bubble above ScrollUp button ─────────────────────
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop =
        document.documentElement.scrollTop || document.body.scrollTop;
      setShowScroll(scrollTop > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ── Auto-scroll to newest message ──────────────────────────────────────────
  useEffect(() => {
    msgEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [history, phase]);

  // ── Lift chat window above the software keyboard on mobile ─────────────────
  // When the on-screen keyboard opens, the visual viewport shrinks.
  // position:fixed elements stay anchored to the layout viewport, so the
  // window can get buried behind the keyboard. We read visualViewport to
  // calculate how much of the layout viewport is hidden and offset `bottom`
  // by that amount so the window always stays fully visible.
  useEffect(() => {
    if (!isOpen || !window.visualViewport) return;
    const vv = window.visualViewport;
    const update = () => {
      // offsetTop = scroll offset of the visual viewport within the layout viewport
      const hidden = window.innerHeight - vv.height - vv.offsetTop;
      setKeyboardOffset(Math.max(0, Math.round(hidden)));
    };
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    update(); // run once on open to capture current state
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      setKeyboardOffset(0);
    };
  }, [isOpen]);

  // ── Auto-open (desktop) / nudge (mobile), fresh chat only, once per session ──
  // Wait until Firestore data has loaded so the chatbot doesn't appear over skeletons.
  useEffect(() => {
    if (aboutLoading) return; // data still loading — defer
    if (sessionStorage.getItem(SESSION_KEY)) return;
    // Don't auto-open if user already has chat history
    if (localStorage.getItem(HISTORY_KEY)) return;
    const isMobile = window.innerWidth <= 768;
    const t = setTimeout(() => {
      // Re-check in case user manually opened the chat during the delay window
      if (sessionStorage.getItem(SESSION_KEY)) return;
      sessionStorage.setItem(SESSION_KEY, "true");
      closeThemesPanel();
      closeAdminPanel();
      processedStepRef.current = null;
      setStepId("welcome"); // prime the step so opening is instant
      if (isMobile) {
        // On mobile: tempt the user with a nudge instead of forcing the popup open
        setNudge(true);
      } else {
        setIsOpen(true);
      }
    }, 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aboutLoading]);

  // ── Step machine ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen || !stepId) return;

    // Prevent replaying the same step when the window is simply re-opened
    if (processedStepRef.current === stepId) return;
    processedStepRef.current = stepId;

    const step = stepById(stepId);
    if (!step) return;

    // Personalize bot messages when user is logged in with Google
    const googleUser = userRef.current;
    const firstName = googleUser?.displayName?.split(" ")[0];
    const personalizedMessage = (() => {
      if (!firstName) return step.message;
      if (step.id === "welcome")
        return `Hi, ${firstName}! Welcome to my portfolio 😉`;
      if (step.id === "welcome-back")
        return `Welcome back, ${firstName}! Great to see you again 👋`;
      return step.message;
    })();

    setPhase("typing");

    timerRef.current = setTimeout(() => {
      // Reveal bot message
      setHistory((prev) => [
        ...prev,
        { role: "bot", text: personalizedMessage, id: keyRef.current++ },
      ]);

      // Auto-answer name questions using Google display name
      if (NAME_STEP_IDS.includes(step.id) && googleUser?.displayName) {
        const displayName = googleUser.displayName;
        timerRef.current = setTimeout(() => {
          setHistory((prev) => [
            ...prev,
            {
              role: "user",
              text: displayName,
              id: keyRef.current++,
              msgStepId: step.id,
              edited: true, // lock edit button since it came from Google
            },
          ]);
          persistChatAnswer(step.id, displayName);
          localStorage.removeItem(STEP_KEY);
          setPhase("idle");
          const nextId =
            typeof step.next === "function"
              ? step.next(displayName)
              : step.next;
          setStepId(nextId);
        }, 600);
        return;
      }

      if (step.type === "message") {
        // Auto-advance after remaining delay
        const remainingDelay = Math.max(0, (step.delay || 650) - TYPING_MS);
        timerRef.current = setTimeout(() => {
          if (step.next === null) {
            // Terminal step — send data then close
            setPhase("idle");
            setIsDone(true);
            localStorage.removeItem(STEP_KEY); // conversation over
            sendChatbotData(userRef.current);
            timerRef.current = setTimeout(() => setIsOpen(false), 600);
          } else {
            const nextId =
              typeof step.next === "function" ? step.next(null) : step.next;
            setStepId(nextId);
          }
        }, remainingDelay);
      } else {
        setPhase("waiting");
      }
    }, TYPING_MS);

    return () => clearTimeout(timerRef.current);
  }, [isOpen, stepId]);

  // ── Open / close helpers ───────────────────────────────────────────────────
  const openChat = useCallback(() => {
    sessionStorage.setItem(SESSION_KEY, "true");
    setNudge(false);
    closeThemesPanel(); // close themes panel if open
    closeAdminPanel(); // close admin panel if open

    const alreadySent = localStorage.getItem(SENT_KEY) === "true";

    if (isDone || alreadySent) {
      // Returning user — restore persisted history and show inline edit UI
      if (history.length === 0) {
        const saved = JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]");
        if (saved.length > 0) {
          keyRef.current = Math.max(...saved.map((m) => m.id), 0) + 1;
          setHistory(saved);
        }
      }
      setIsOpen(true);
      return;
    }

    // If there's existing history (mid-conversation, page was refreshed), just
    // re-open as-is — don't replay the welcome step on top of saved messages.
    if (history.length > 0) {
      setIsOpen(true);
      return;
    }

    if (!stepId) {
      processedStepRef.current = null;
      setStepId("welcome");
    }
    // If mid-conversation, just reopen without resetting
    setIsOpen(true);
  }, [isDone, stepId, history.length, closeThemesPanel, closeAdminPanel]);

  // ── Inline edit confirm ────────────────────────────────────────────────────
  const handleEditConfirm = useCallback((msg, newText) => {
    const trimmed = typeof newText === "string" ? newText.trim() : "";
    setEditingMsgId(null);
    setEditHoverStar(0);
    if (!trimmed || trimmed === msg.text) return;

    const step = stepById(msg.msgStepId);
    if (!step) return;

    // Compute whether the path changes
    const oldNext =
      typeof step.next === "function" ? step.next(msg.text) : step.next;
    const newNext =
      typeof step.next === "function" ? step.next(trimmed) : step.next;
    const pathChanged = oldNext !== newNext;

    if (!pathChanged) {
      // ── Silent update: just patch the bubble value, keep history intact ──
      setHistory((prev) =>
        prev.map((m) =>
          m.id === msg.id
            ? { ...m, text: trimmed, skipped: false, edited: true }
            : m,
        ),
      );
      // Re-persist answers with updated value
      try {
        const existing = JSON.parse(localStorage.getItem(ANSWERS_KEY) || "{}");
        const field = STEP_FIELD[msg.msgStepId];
        if (field) existing[field] = trimmed;
        localStorage.setItem(ANSWERS_KEY, JSON.stringify(existing));
      } catch (_) {}
      localStorage.removeItem(SENT_KEY);
      // Email will be sent on chat close — not immediately on each edit
      return;
    }

    // ── Path changed: slice history and replay from new next step ──
    setHistory((prev) => {
      const idx = prev.findIndex((m) => m.id === msg.id);
      if (idx === -1) return prev;
      const sliced = prev
        .slice(0, idx + 1)
        .map((m, i) =>
          i === idx ? { ...m, text: trimmed, skipped: false, edited: true } : m,
        );
      try {
        localStorage.removeItem(ANSWERS_KEY);
        sliced.forEach((m) => {
          if (m.role === "user" && m.msgStepId)
            persistChatAnswer(m.msgStepId, m.text);
        });
      } catch (_) {}
      return sliced;
    });

    localStorage.removeItem(SENT_KEY);
    localStorage.removeItem(STEP_KEY);
    setIsDone(false);
    processedStepRef.current = null;
    setStepId(newNext);
  }, []);

  const closeChat = useCallback(() => {
    clearTimeout(timerRef.current);
    setIsOpen(false);
    if (isDone) sendChatbotData(userRef.current);
  }, [isDone]);

  // ── Click-outside to close ─────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (e) => {
      if (
        windowRef.current &&
        !windowRef.current.contains(e.target) &&
        bubbleRef.current &&
        !bubbleRef.current.contains(e.target)
      ) {
        closeChat();
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isOpen, closeChat]);

  // ── Core advance ───────────────────────────────────────────────────────────
  const advance = useCallback(
    (answerText, action) => {
      const step = stepById(stepId);
      if (!step) return;

      // Execute special resume actions
      if (action === "view-resume") {
        const url = resolveView(aboutData?.resumeId || aboutData?.resumeUrl);
        if (url) window.open(url, "_blank", "noopener,noreferrer");
      } else if (action === "download-resume") {
        const url = resolveDownload(
          aboutData?.resumeId || aboutData?.resumeUrl,
        );
        if (url) {
          const a = document.createElement("a");
          a.href = url;
          a.download = "Santhosh_Resume";
          a.target = "_blank";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      }

      // Append user bubble — always, including skips, so users can revisit.
      // Skipped bubbles carry skipped:true with empty text; one-time editable.
      const msgStepId = step.id;
      if (answerText) {
        setHistory((prev) => [
          ...prev,
          { role: "user", text: answerText, id: keyRef.current++, msgStepId },
        ]);
        // Persist answer immediately so a browser-close loses nothing
        persistChatAnswer(step.id, answerText);
      } else if (
        step.skippable ||
        step.options?.some((o) => o.value === "__skip__")
      ) {
        // Skippable step — show a muted "Skipped" bubble the user can edit once
        setHistory((prev) => [
          ...prev,
          {
            role: "user",
            text: "",
            skipped: true,
            id: keyRef.current++,
            msgStepId,
          },
        ]);
      }

      // Clear STEP_KEY so a refresh between now and the next phase==="waiting"
      // doesn't restore the step the user just answered.
      localStorage.removeItem(STEP_KEY);

      // Resolve next step
      const nextId =
        typeof step.next === "function" ? step.next(answerText) : step.next;
      setStepId(nextId);
    },
    [stepId, aboutData],
  );

  // ── Input handlers ─────────────────────────────────────────────────────────
  const handleOption = useCallback(
    (opt) => {
      const isSkip = opt.value === "__skip__";
      advance(isSkip ? null : opt.label, opt.action);
    },
    [advance],
  );

  const handleTextSubmit = useCallback(
    (skip = false) => {
      advance(skip ? null : inputValue.trim() || null, null);
      setInputValue("");
    },
    [advance, inputValue],
  );

  const handleRating = useCallback(
    (star, skip = false) => {
      if (skip) {
        advance(null, null);
      } else {
        const labels = [
          "",
          "😞 Poor",
          "😕 Fair",
          "🙂 Good",
          "😄 Great",
          "🤩 Excellent",
        ];
        const stars = "★".repeat(star) + "☆".repeat(5 - star);
        advance(`${stars}  ${labels[star]}`, null);
      }
      setHoverRating(0);
    },
    [advance],
  );

  // ── Guard: hide during admin edit mode ────────────────────────────────────
  if (isAdmin && adminMode) return null;

  const currentStep = stepById(stepId);

  // ── Portal output ──────────────────────────────────────────────────────────
  return createPortal(
    <>
      {/* ── Bubble trigger ── */}
      <div
        ref={bubbleRef}
        className={`chatbot__bubble-wrap${showScroll ? " chatbot__bubble-wrap--lifted" : ""}`}
      >
        <button
          className={`themes__button chatbot__bubble${
            nudge && !isOpen ? " chatbot__bubble--nudge" : ""
          }`}
          onClick={isOpen ? closeChat : openChat}
          aria-label="Open portfolio chat guide"
          title="Chat guide"
        >
          <i
            className={`${isOpen ? "bx bxs-chat" : "bx bx-chat"} themes__icon`}
          ></i>
        </button>
      </div>

      {/* ── Chat window ── */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            ref={windowRef}
            className={`chatbot__window${showScroll ? " chatbot__window--lifted" : ""}`}
            style={
              keyboardOffset > 0
                ? { bottom: `${keyboardOffset + 16}px` }
                : undefined
            }
            variants={chatWindowVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            {/* Header */}
            <div className="chatbot__header">
              <div>
                <div className="chatbot__header-title">
                  Santhosh's Portfolio
                </div>
                {ipLocation ? (
                  <div className="chatbot__header-sub">
                    <i className="uil uil-location-pin-alt"></i> {ipLocation}
                    &nbsp;
                    <span className="chatbot__header-location-note">
                      (approx. from your IP)
                    </span>
                  </div>
                ) : (
                  <div className="chatbot__header-sub">Interactive guide</div>
                )}
              </div>
              <button
                className="chatbot__close-btn"
                onClick={closeChat}
                aria-label="Close chat"
              >
                <i className="uil uil-times"></i>
              </button>
            </div>

            {/* Message thread */}
            <div className="chatbot__messages">
              <AnimatePresence initial={false}>
                {history.map((msg) => (
                  <motion.div
                    key={msg.id}
                    className={`chatbot__msg chatbot__msg--${msg.role}${editingMsgId === msg.id ? " chatbot__msg--editing" : ""}`}
                    variants={chatMsgVariants}
                    initial="hidden"
                    animate="visible"
                  >
                    {msg.role === "user" && editingMsgId === msg.id ? (
                      /* ── Inline edit ── */
                      (() => {
                        const editStep = stepById(msg.msgStepId);
                        if (editStep?.type === "options") {
                          return (
                            /* Options chips */
                            <div className="chatbot__edit-inline-row chatbot__edit-inline-row--options">
                              <div className="chatbot__options">
                                {editStep.options
                                  .filter((o) => o.value !== "__skip__")
                                  .map((opt) => (
                                    <button
                                      key={opt.value}
                                      className="chatbot__option-btn"
                                      onClick={() =>
                                        handleEditConfirm(msg, opt.label)
                                      }
                                    >
                                      {opt.icon && (
                                        <i
                                          className={`${opt.icon} chatbot__opt-icon`}
                                        ></i>
                                      )}
                                      {opt.label}
                                    </button>
                                  ))}
                                <button
                                  className="chatbot__review-cancel"
                                  onMouseDown={() => setEditingMsgId(null)}
                                  aria-label="Cancel edit"
                                >
                                  <i className="uil uil-times"></i>
                                </button>
                              </div>
                            </div>
                          );
                        }
                        if (editStep?.type === "rating") {
                          const currentStars = (msg.text.match(/★/g) || [])
                            .length;
                          const labels = [
                            "",
                            "😞 Poor",
                            "😕 Fair",
                            "🙂 Good",
                            "😄 Great",
                            "🤩 Excellent",
                          ];
                          return (
                            /* Star picker */
                            <div className="chatbot__edit-inline-row">
                              <div className="chatbot__stars">
                                {[1, 2, 3, 4, 5].map((star) => {
                                  const filled =
                                    star <= (editHoverStar || currentStars);
                                  return (
                                    <button
                                      key={star}
                                      className={`chatbot__star-btn${filled ? " chatbot__star-btn--filled" : ""}`}
                                      onMouseEnter={() =>
                                        setEditHoverStar(star)
                                      }
                                      onMouseLeave={() => setEditHoverStar(0)}
                                      onClick={() => {
                                        const picked = editHoverStar || star;
                                        handleEditConfirm(
                                          msg,
                                          "★".repeat(picked) +
                                            "☆".repeat(5 - picked) +
                                            "  " +
                                            labels[picked],
                                        );
                                      }}
                                      aria-label={`Rate ${star}`}
                                    >
                                      <i
                                        className={
                                          filled ? "bx bxs-star" : "bx bx-star"
                                        }
                                      ></i>
                                    </button>
                                  );
                                })}
                              </div>
                              <button
                                className="chatbot__review-cancel"
                                onMouseDown={() => {
                                  setEditingMsgId(null);
                                  setEditHoverStar(0);
                                }}
                                aria-label="Cancel edit"
                              >
                                <i className="uil uil-times"></i>
                              </button>
                            </div>
                          );
                        }
                        /* Text input */
                        return (
                          <div className="chatbot__edit-inline-row">
                            <input
                              ref={editInputRef}
                              className="chatbot__text-input"
                              defaultValue={msg.skipped ? "" : msg.text}
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === "Enter")
                                  handleEditConfirm(
                                    msg,
                                    editInputRef.current?.value ?? "",
                                  );
                                if (e.key === "Escape") setEditingMsgId(null);
                              }}
                            />
                            <button
                              className="chatbot__send-btn"
                              onMouseDown={() =>
                                handleEditConfirm(
                                  msg,
                                  editInputRef.current?.value ?? "",
                                )
                              }
                              aria-label="Confirm edit"
                            >
                              <i className="uil uil-check"></i>
                            </button>
                            <button
                              className="chatbot__review-cancel"
                              onMouseDown={() => setEditingMsgId(null)}
                              aria-label="Cancel edit"
                            >
                              <i className="uil uil-times"></i>
                            </button>
                          </div>
                        );
                      })()
                    ) : (
                      /* ── Normal bubble + edit btn ── */
                      <div className="chatbot__msg-bubble-wrap">
                        {msg.role === "user" && (
                          <div
                            className="chatbot__user-avatar"
                            aria-hidden="true"
                          >
                            {user?.photoURL ? (
                              <img
                                src={user.photoURL}
                                alt=""
                                className="chatbot__user-avatar__img"
                                referrerPolicy="no-referrer"
                              />
                            ) : (
                              <span className="chatbot__user-avatar__initial">
                                {user?.displayName?.[0]?.toUpperCase() ||
                                  user?.email?.[0]?.toUpperCase() ||
                                  "U"}
                              </span>
                            )}
                          </div>
                        )}
                        {msg.role === "user" &&
                          msg.msgStepId &&
                          !msg.edited && (
                            <button
                              className="chatbot__msg-edit-btn"
                              onClick={() => setEditingMsgId(msg.id)}
                              aria-label={
                                msg.skipped
                                  ? "Answer this question"
                                  : "Edit this response"
                              }
                            >
                              <i className="uil uil-edit-alt"></i>
                            </button>
                          )}
                        <div
                          className={`chatbot__msg-bubble${msg.skipped ? " chatbot__msg-bubble--skipped" : ""}`}
                        >
                          {msg.skipped ? "😑 Skipped" : msg.text}
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}

                {phase === "typing" && (
                  <motion.div
                    key="typing-indicator"
                    className="chatbot__typing"
                    variants={chatMsgVariants}
                    initial="hidden"
                    animate="visible"
                    exit={{ opacity: 0, transition: { duration: 0.15 } }}
                  >
                    <span className="chatbot__typing-dot" />
                    <span className="chatbot__typing-dot" />
                    <span className="chatbot__typing-dot" />
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Scroll anchor */}
              <div ref={msgEndRef} />
            </div>

            {/* ── Input area (only while waiting for user) ── */}
            <AnimatePresence>
              {phase === "waiting" && currentStep && (
                <motion.div
                  className="chatbot__input-area"
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0, transition: { duration: 0.2 } }}
                  exit={{ opacity: 0 }}
                >
                  {/* OPTIONS */}
                  {currentStep.type === "options" && (
                    <div className="chatbot__options">
                      {currentStep.options.map((opt) => (
                        <button
                          key={opt.value}
                          className={`chatbot__option-btn${
                            opt.value === "__skip__"
                              ? " chatbot__option-btn--skip"
                              : ""
                          }`}
                          onClick={() => handleOption(opt)}
                        >
                          {opt.icon && (
                            <i className={`${opt.icon} chatbot__opt-icon`}></i>
                          )}
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* TEXT INPUT */}
                  {currentStep.type === "text-input" && (
                    <>
                      <div className="chatbot__text-row">
                        <input
                          type="text"
                          className="chatbot__text-input"
                          placeholder={currentStep.placeholder || "Type here…"}
                          value={inputValue}
                          onChange={(e) => setInputValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && inputValue.trim()) {
                              handleTextSubmit(false);
                            }
                          }}
                          autoFocus
                        />
                        <button
                          className="chatbot__send-btn"
                          onClick={() => handleTextSubmit(false)}
                          disabled={!inputValue.trim()}
                          aria-label="Send"
                        >
                          <i className="uil uil-message"></i>
                        </button>
                      </div>
                      {currentStep.skippable && (
                        <button
                          className="chatbot__skip-link"
                          onClick={() => handleTextSubmit(true)}
                        >
                          😒 Skip this question
                        </button>
                      )}
                    </>
                  )}

                  {/* STAR RATING */}
                  {currentStep.type === "rating" && (
                    <>
                      <div className="chatbot__stars">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            className={`chatbot__star-btn${
                              star <= hoverRating
                                ? " chatbot__star-btn--filled"
                                : ""
                            }`}
                            onMouseEnter={() => setHoverRating(star)}
                            onMouseLeave={() => setHoverRating(0)}
                            onClick={() => handleRating(star)}
                            aria-label={`Rate ${star} star${star > 1 ? "s" : ""}`}
                          >
                            <i
                              className={
                                star <= hoverRating
                                  ? "bx bxs-star"
                                  : "bx bx-star"
                              }
                            ></i>
                          </button>
                        ))}
                      </div>
                      {currentStep.skippable && (
                        <button
                          className="chatbot__skip-link"
                          onClick={() => handleRating(0, true)}
                        >
                          😒 Skip this question
                        </button>
                      )}
                    </>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </>,
    document.body,
  );
};

export default Chatbot;
